import { useTranslate } from '@athena/ui'

/**
 * Morning, afternoon or evening, from the reader's own clock.
 *
 * A pure function of an hour rather than a `new Date()` inside the component,
 * so the boundaries are something a test can state instead of something that
 * only shows up at 04:59. The cut-offs are noon and six.
 *
 * It returns a *key* rather than words, and there are two per period. That is
 * the alternative to building "Good morning" + ", " + name in the component:
 * Spanish and English happen to both put the name last, and a language that
 * did not would be unable to express it if the greeting arrived pre-assembled
 * (V1.md section 12).
 */
export function greetingKeyFor(
  date: Date,
  named: boolean,
):
  | 'dashboard.greeting.morning'
  | 'dashboard.greeting.morningNamed'
  | 'dashboard.greeting.afternoon'
  | 'dashboard.greeting.afternoonNamed'
  | 'dashboard.greeting.evening'
  | 'dashboard.greeting.eveningNamed' {
  const hour = date.getHours()
  const period = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening'

  if (period === 'morning') {
    return named ? 'dashboard.greeting.morningNamed' : 'dashboard.greeting.morning'
  }
  if (period === 'afternoon') {
    return named ? 'dashboard.greeting.afternoonNamed' : 'dashboard.greeting.afternoon'
  }
  return named ? 'dashboard.greeting.eveningNamed' : 'dashboard.greeting.evening'
}

export interface DashboardHeaderProps {
  /** The signed-in tutor's name. Null while it is unknown, never a placeholder. */
  name: string | null
  /** Injectable so the greeting is testable; defaults to now. */
  now?: Date
}

/**
 * The one line at the top: who is reading, and what this page is for.
 *
 * Compact on purpose — a page whose job is "what needs attention" should not
 * spend a third of the first screen introducing itself. No name yields
 * "Good morning" on its own rather than "Good morning, there": a greeting with
 * a placeholder in it reads worse than a greeting without one.
 */
export function DashboardHeader({ name, now = new Date() }: DashboardHeaderProps) {
  const t = useTranslate()

  return (
    <div className="flex flex-col gap-1">
      {/* An h2, not an h1. The shell already gives the page its h1 ("Dashboard"),
          and a greeting is not a second title for the same page — it is the
          first thing on it. Same size as before; only the level moved. */}
      <h2 className="text-h2 font-bold text-text">
        {name === null ? t(greetingKeyFor(now, false)) : t(greetingKeyFor(now, true), [name])}
      </h2>
      <p className="text-body text-text-secondary">{t('dashboard.subtitle')}</p>
    </div>
  )
}
